import React from "react";
import PropTypes from "prop-types";
import { Alert } from "@material-ui/lab";
import { CircularProgress } from "@material-ui/core";

// Live status banner for a bulk-upload background job on a list page.
// Shared by the Athletes (Student) and Staff bulk flows so the
// success/error states stay in sync.
//
// Step convention (set by each page's _checkStateBulk poller):
//   5    → job running
//   6    → job succeeded (every row imported)
//   6.1  → job finished with errors — with per-row handling this can be a
//          partial failure (some records created, some rows failed); the
//          per-row errors come through in errorsBulk.
// Anything else renders nothing.
//
// `entity` is the lowercase noun used in the copy ("athletes", "staff")
// and reads naturally in "Creating <entity> in bulk".
export const BulkJobStatus = ({ stepsBulk, onFinish, errorsBulk, entity }) => {
  // The finished banners are clickable (role="button"), so they must
  // also be keyboard-operable: focusable via tabIndex and activated on
  // Enter/Space — not just mouse click.
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (onFinish) onFinish();
    }
  };

  if (stepsBulk === 5) {
    return (
      <Alert
        severity="info"
        icon={<CircularProgress color="inherit" size={18} />}
        style={{ alignItems: "center" }}
      >
        Creating {entity} in bulk…
      </Alert>
    );
  }

  if (stepsBulk === 6) {
    return (
      <Alert
        severity="success"
        onClick={onFinish}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        style={{ cursor: "pointer", alignItems: "center" }}
        role="button"
      >
        Upload complete — click here to refresh the list.
      </Alert>
    );
  }

  if (stepsBulk === 6.1) {
    return (
      <Alert
        severity="error"
        onClick={onFinish}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        style={{ cursor: "pointer", alignItems: "center" }}
        role="button"
      >
        Upload finished with errors.
        {errorsBulk && errorsBulk.length > 0
          ? ` ${errorsBulk.join(" ")}`
          : " Please try again or contact support."}
      </Alert>
    );
  }

  return null;
};

BulkJobStatus.propTypes = {
  // Current step of the bulk wizard (5, 6, or 6.1 are rendered).
  stepsBulk: PropTypes.number,
  // Click handler for the finished banners — resets the wizard and
  // refetches the list.
  onFinish: PropTypes.func,
  // Error strings surfaced from the failed background operation.
  errorsBulk: PropTypes.array,
  // Lowercase noun for the records being created ("athletes", "staff").
  entity: PropTypes.string.isRequired,
};
